import { useAuth0 } from "@auth0/auth0-react";
import {
  FormControl,
  FormLabel,
  Input,
  Stack,
  useColorMode,
} from "@chakra-ui/react";
import WarningTextComponent, {
  WarningTextProps,
} from "components/shared/WarningTextComponent";
import {
  Button,
  ButtonSize,
  ButtonType,
} from "theme/old-design-system/styled-components";
import useAdminAgentsStore from "hooks/use-admin-agent-store";
import useAdminMerchantStore from "hooks/use-admin-merchant-store";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React, { ChangeEvent, useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import Select, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import AuthorizationService from "services/authorization";
import { MerchantDto } from "entities/dto/MerchantDTO";
import { getReactSelectStyles } from "util/methods";

interface MerchantOptionTypes {
  value?: number;
  label?: string;
}

interface ChannelOptionTypes {
  value: string;
  label: string;
}

interface RoleOptionTypes {
  value: number;
  label: string;
}

const AddAgentForm = () => {
  const auth0 = useAuth0();
  const channelOptions: ChannelOptionTypes[] = [
    { value: "sms", label: "SMS" },
    { value: "whatsapp", label: "WhatsApp" },
  ];
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();
  const { adminMerchants } = useAdminMerchantStore();
  const { createAgent, errors } = useAdminAgentsStore();
  const [firstNameValue, setFirstNameValue] = useState<string>("");
  const [surnameValue, setSurnameValue] = useState<string>("");
  const [emailValue, setEmailValue] = useState<string>("");
  const [merchantIdValue, setMerchantIdValue] = useState<number>(0);
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>("");
  const [selectedMerchantValue, setSelectedMercantValue] =
    useState<SingleValue<MerchantOptionTypes>>(null);
  const [selectedContactValue, setSelectedContactValue] = useState<
    SingleValue<ChannelOptionTypes>
  >(channelOptions[1]);
  const [roleOptions, setRoleOptions] = useState<RoleOptionTypes[]>();
  const [selectedRoleValue, setSelectedRoleValue] =
    useState<RoleOptionTypes | null>(null);
  const [warning, setWarning] = useState<WarningTextProps | undefined>(
    undefined
  );

  useEffect(() => {
    setWarning(undefined);
  }, []);

  useEffect(() => {
    setSelectedRoleValue(null);
    setRoleOptions([] as RoleOptionTypes[]);
    if (merchantIdValue) {
      AuthorizationService.getRoles(auth0, merchantIdValue).then(
        (rolesReturned) => {
          setRoleOptions(
            rolesReturned.map<RoleOptionTypes>((role) => ({
              value: role.id,
              label: role.name,
            }))
          );
        }
      );
    }
  }, [merchantIdValue]);

  const isValidPhone = () => {
    return phoneNumberValue &&
      phoneNumberValue !== "" &&
      !isValidPhoneNumber(phoneNumberValue)
      ? setWarning({
          key: "invalid_phone_number",
          description: "* Invalid phone number format",
        })
      : setWarning(undefined);
  };

  const handleIdChange = (e: SingleValue<MerchantOptionTypes>) => {
    setMerchantIdValue(Number(e?.value));
    setSelectedMercantValue(e);
  };
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFirstNameValue(event.target.value);

  const handleSurnameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSurnameValue(event.target.value);

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEmailValue(event.target.value);

  const handleContactChange = (e: SingleValue<any>) => {
    setSelectedContactValue(e);
  };

  const merchantOptions: MerchantOptionTypes[] = adminMerchants.map(
    (merchant: MerchantDto) => ({
      value: merchant.id,
      label: merchant.name,
    })
  );

  const isDisabled = (): boolean =>
    !merchantIdValue ||
    !firstNameValue ||
    !surnameValue ||
    !emailValue ||
    !selectedRoleValue ||
    !!warning;

  const resetValues = () => {
    setFirstNameValue("");
    setSurnameValue("");
    setEmailValue("");
    setMerchantIdValue(0);
    setPhoneNumberValue("");
    setSelectedMercantValue(null);
    setSelectedContactValue(channelOptions[1]);
    setSelectedRoleValue(null);
  };

  const saveAgent = () => {
    createAgent({
      merchant_id: merchantIdValue,
      first_name: firstNameValue,
      last_name: surnameValue,
      email: emailValue,
      phone: phoneNumberValue,
      contact_channel: selectedContactValue!.value,
      role_id: selectedRoleValue?.value,
    });
    if (errors.length) {
      toast.error(errors[0]);
    } else {
      toast.success("Agent Created Successfully");
    }
    resetValues();
  };

  return (
    <>
      <div id="agent-form-input">
        <Stack spacing={3}>
          <FormControl isRequired>
            <FormLabel> Select Merchant </FormLabel>
            <Select
              placeholder="Select Merchants"
              onChange={handleIdChange}
              options={merchantOptions}
              value={selectedMerchantValue}
              styles={getReactSelectStyles(colorMode, colorScheme)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel mb="8px"> Agent First Name</FormLabel>
            <Input
              value={firstNameValue}
              onChange={handleNameChange}
              placeholder="John"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel mb="8px"> Agent Last Name</FormLabel>
            <Input
              value={surnameValue}
              onChange={handleSurnameChange}
              placeholder="Doe"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel> Agent Email Address </FormLabel>
            <Input
              value={emailValue}
              onChange={handleEmailChange}
              placeholder="johndoe@fuzey.io"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel> Role </FormLabel>
            <Select
              placeholder="Select Role"
              onChange={(v) => setSelectedRoleValue(v || null)}
              options={roleOptions}
              value={selectedRoleValue}
              styles={getReactSelectStyles(colorMode, colorScheme)}
              isDisabled={!selectedMerchantValue}
            />
          </FormControl>
          <FormControl
            sx={{
              ".PhoneInputInput": {
                backgroundColor: "inherit",
                color: "inherit",
              },
            }}
          >
            <FormLabel> Agent Phone Number </FormLabel>

            {warning && warning.key === "invalid_phone_number" && (
              <WarningTextComponent
                isValid={false}
                text={warning.description}
              />
            )}

            <PhoneInput
              id="phoneInput"
              style={{ borderRadius: "18px" }}
              placeholder="Enter agent's phone number"
              onChange={(value: string) => {
                setPhoneNumberValue(value);
              }}
              value={phoneNumberValue}
              autoFocus={true}
              onBlur={isValidPhone}
            />
          </FormControl>
          <FormControl>
            <FormLabel> Preferred Contact Channel </FormLabel>
            <Select
              placeholder="Select Channel"
              onChange={handleContactChange}
              options={channelOptions}
              value={selectedContactValue}
              styles={getReactSelectStyles(colorMode, colorScheme)}
            />
          </FormControl>
        </Stack>
        <Button
          type={ButtonType.PRIMARY}
          onClick={saveAgent}
          isDisabled={isDisabled()}
          mt={30}
          mb={30}
          size={ButtonSize.LARGE}
        >
          {" "}
          Save Agent{" "}
        </Button>
      </div>
    </>
  );
};

export default AddAgentForm;
